import {ColumnDef} from '@tanstack/react-table';
import {ReadCampaign} from '@tinct/core-api-models';
import {BadgeStatus, Button, LongText} from '@tinct/web-components';
import {CollectionColumnHeader, CollectionRowActions} from '@tinct/web-collection';
import {Link} from 'react-router-dom';
import {IconEye, IconPencil, IconTrash, IconWorldOff} from '@tabler/icons-react';
import {Building2} from 'lucide-react';
import {TrendingDown, TrendingUp} from 'lucide-react';
import {CampaignAxisBadge} from '../../shared';
import {getCampaignActionCapabilities} from '../../details-campaign-page/components/campaign-actions-menu';
import {DeleteCampaignModal} from '../../../modals/delete-campaign-modal/delete-campaign-modal';
import {UnpublishCampaignModal} from '../../../modals/unpublish-campaign-modal/unpublish-campaign-modal';
import {formatRelativeTime} from '@tinct/core-utils';

export type UseCampaignsListColumnsOptions = {
  /**
   * Prefix every row link is built from, e.g. `/w/<organizationId>`. Passed in rather
   * than resolved here so the hook stays free of workspace context — see the
   * "Reuse between surfaces" section of the page-components rule.
   */
  basePath: string;
};

export function useCampaignsListColumns(
  { basePath }: UseCampaignsListColumnsOptions,
): ColumnDef<ReadCampaign>[] {
  return [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Campaign" />
      ),
      cell: ({ row }) => {
        const name = row.original.name ?? '-';
        return (
          <div className="flex items-center gap-1.5">
            <Button variant="link" className="p-0 h-auto" asChild>
              <Link to={`${basePath}/campaigns/${row.original.id}`}>
                <LongText className="max-w-[200px] font-semibold">{name}</LongText>
              </Link>
            </Button>
            <CampaignAxisBadge axis={row.original.landingPageVariationAxis} />
          </div>
        );
      },
      size: 150,
      minSize: 120,
      enableHiding: false
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const campaign = row.original;

        if(campaign.status === "published") {
          return <BadgeStatus variant="green">Published</BadgeStatus>
        }

        if(campaign.status === "unpublished") {
          return <BadgeStatus variant="red">Unpublished</BadgeStatus>
        }

        if(campaign.status === "ready") {
          return <BadgeStatus variant="blue">Ready</BadgeStatus>
        }

        return <BadgeStatus variant="yellow">Draft</BadgeStatus>
      },
      size: 120,
      enableHiding: false
    },
    {
      accessorKey: 'audience',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Audience" />
      ),
      cell: ({ row }) => {
        // TODO: Add audience field to campaign model when available
        const audience = (row.original as ReadCampaign & { audience?: { name?: string } })?.audience;
        return (
          <div className="text-muted-foreground">
            {audience?.name ?? '-'}
          </div>
        );
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: 'pages',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Pages / companies" />
      ),
      cell: ({ row }) => {
        const statistics = row.original.statistics;
        const totalPages = statistics?.totalPagesCount ?? 0;
        const leadsCount = statistics?.leadsCount ?? 0;
        return (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <span>{`${totalPages}/${leadsCount}`}</span>
            {totalPages !== leadsCount && (
              <Building2 className="size-4" />
            )}
          </div>
        );
      },
      size: 80,
      enableSorting: false,
    },
    {
      id: 'visitsCount',
      accessorFn: (row) => (row as ReadCampaign & { statistics?: { visitsCount?: number } })?.statistics?.visitsCount,
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Visits" />
      ),
      cell: ({ row }) => {
        // TODO: Add statistics field to campaign model when available
        const statistics = (row.original as ReadCampaign & { statistics?: { visitsCount?: number; visitsTrend?: number } })?.statistics;
        const visitsCount = statistics?.visitsCount ?? 0;
        const visitsTrend = statistics?.visitsTrend ?? 0;

        return (
          <div className="flex items-center gap-1">
            {visitsTrend > 0 ? (
              <TrendingUp className="size-4 text-green-500" />
            ) : visitsTrend < 0 ? (
              <TrendingDown className="size-4 text-red-500" />
            ) : null}
            <span>{visitsCount}</span>
          </div>
        );
      },
      size: 100,
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt;
        if (!createdAt) return <div className="text-muted-foreground">-</div>;

        const date = new Date(createdAt);
        return (
          <div className="text-muted-foreground">
            {date.toLocaleDateString('en-GB')}
          </div>
        );
      },
      size: 110,
      enableHiding: false
    },
    {
      accessorKey: 'updatedAt',
      header: ({ column }) => (
        <CollectionColumnHeader column={column} title="Last edited" />
      ),
      cell: ({ row }) => {
        const updatedAt = row.original.updatedAt;
        if (!updatedAt) return <div className="text-muted-foreground">-</div>;

        const date = new Date(updatedAt);
        return (
          <div className="text-muted-foreground">
            {formatRelativeTime(date)}
          </div>
        );
      },
      size: 110,
      enableHiding: false
    },
    {
      id: 'actions',
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const campaign = row.original;
        const status = campaign.status;
        // Same rule as the details page menu: a campaign visitors have seen is
        // kept (greyed Delete + reason) rather than silently losing the action.
        const deleteAction = {
          label: 'Delete',
          icon: IconTrash,
          group: 'danger',
          className: 'text-destructive',
          disabledReason: getCampaignActionCapabilities(campaign).delete.reason,
          dialog: {
            id: 'delete-campaign',
            component: DeleteCampaignModal,
          },
        };

        return (
          // Rows navigate to the campaign details — keep every action click
          // from bubbling into that row navigation.
          <div
            className="flex justify-end items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            {status === 'draft' && (
              <Link to={`${basePath}/funnel-campaigns/${row.original.id}`}>
                <Button variant="outline" size="sm">
                  Finish setup
                </Button>
              </Link>
            )}
            {status === 'ready' && (
              <Link to={`${basePath}/funnel-campaigns/${row.original.id}`}>
                {/* The dispatcher resumes at the publish step — the label
                    says what the click leads to, not what it performs. */}
                <Button variant="outline" size="sm">
                  Review & publish
                </Button>
              </Link>
            )}

            <CollectionRowActions
              row={row}
              actions={[
                ...(status === 'draft'
                  ? [
                      {
                        label: 'Dashboard',
                        icon: IconEye,
                        linkTo: (campaign: ReadCampaign) =>
                          `${basePath}/campaigns/${campaign.id}`,
                      },
                      deleteAction,
                    ]
                  : []),
                ...(status === 'ready'
                  ? [
                      {
                        label: 'Dashboard',
                        icon: IconEye,
                        linkTo: (campaign: ReadCampaign) =>
                          `${basePath}/campaigns/${campaign.id}`,
                      },
                      deleteAction,
                    ]
                  : []),
                ...(status === 'unpublished'
                  ? [
                      {
                        label: 'Dashboard',
                        icon: IconEye,
                        linkTo: (campaign: ReadCampaign) =>
                          `${basePath}/campaigns/${campaign.id}`,
                      },
                      {
                        label: 'Edit',
                        icon: IconPencil,
                        linkTo: (campaign: ReadCampaign) =>
                          `${basePath}/campaigns/${campaign.id}/settings`,
                      },
                      deleteAction,
                    ]
                  : []),
                ...(status === 'published'
                  ? [
                      {
                        label: 'Dashboard',
                        icon: IconEye,
                        linkTo: (campaign: ReadCampaign) =>
                          `${basePath}/campaigns/${campaign.id}`,
                      },
                      {
                        label: 'Unpublish',
                        icon: IconWorldOff,
                        dialog: {
                          id: 'unpublish-campaign',
                          component: UnpublishCampaignModal,
                        },
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        );
      },
      size: 140,
    },
  ];
}
